import * as React from 'react';

import {
    BoardCaseDetailsExportTypes,
    BoardCriteriaEnum,
    BoardFilterDateTypesEnum,
    BoardViewDataTypesEnum,
    CaseStatusTypesEnum,
    CaseSubStatusTypesEnum,
    DateTypesEnum,
    UserRightsEnum,
    UserSettingTypesEnum,
    ZoneContextType,
} from '../../utilities/Constants';
import {
    IBoardComment,
    IBoardExportPreferencesModel,
    IBoardParametersModel,
} from '../../interfaces/Report/IBoard';
import { Modal, Stack } from 'react-bootstrap';

import ApiClient from '../../services/apiClient';
import { ApiRoutes } from '../../utilities/ApiRoutes';
import Authorization from '../../stores/Authorization';
import BoardHelper from '../../utilities/BoardHelper';
import { BoardPreferencesHeader } from '../../components/board/BoardPreferencesHeader';
import CheckmateSelect from '../../components/shared/CheckmateSelect';
import Common from '../../stores/Common';
import { DisplayMessages } from '../../utilities/DisplayMessages';
import { DocumentTitle } from '../../components/shared/DocumentTitle';
import Html5ReportViewer from '../../components/report/Html5ReportViewer';
import { ICaseStatusLookupModel } from '../../interfaces/ICase';
import { IMultiSelectOption } from '../../interfaces/ILookup';
import { IOrganizationBaseModel } from '../../interfaces/IOrganization';
import { IUserModel } from '../../interfaces/IUser';
import { IUserSettingModel } from '../../interfaces/ITask';
import { IValidation } from '../../interfaces/IError';
import { Link } from 'react-router-dom';
import { Loader } from '../../components/shared/Loader';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import ParamUtils from '../../shared/ParamUtilities';
import ValidateUtils from '../../shared/validations';
import { cloneDeep } from 'lodash';

const _apiClient = new ApiClient();

interface IBoardProps {
    user: IUserModel;
    zoneContext: ZoneContextType;
}

interface IBoardState {
    pendingResponse: boolean;
    showSettingsModal: boolean;
    defaultOrganization: IOrganizationBaseModel;
    validation: IValidation;
    initBoardPreferences: IBoardParametersModel;
    boardPreferences: IBoardParametersModel;
    boardExportPreferences: IBoardExportPreferencesModel;
    readyToRenderReport: boolean;
    refreshReport: boolean;
    saveToFavourites: boolean;
    userBoardSetting: IUserSettingModel;
    initComments: IBoardComment[];
    comments: IBoardComment[];
    showCommentsModal: boolean;
    showExportModal: boolean;
    caseStatuses: ICaseStatusLookupModel[];
    selectedCaseStatuses: IMultiSelectOption[];
}

export class Board extends React.Component<IBoardProps, IBoardState> {
    constructor(props: any) {
        super(props);

        this.state = {
            validation: {},
            showSettingsModal: true,
            defaultOrganization: {},
            boardPreferences: {
                statusId: CaseStatusTypesEnum.Open.Value,
                criteria: BoardCriteriaEnum.CaseVolume.Value,
                organizationGuid: this.props.zoneContext.zoneGuid,
            },
            initBoardPreferences: {
                statusId: CaseStatusTypesEnum.Open.Value,
                criteria: BoardCriteriaEnum.CaseVolume.Value,
                organizationGuid: this.props.zoneContext.zoneGuid,
            },
            boardExportPreferences: {
                statusId: CaseStatusTypesEnum.Open.Value,
                criteria: BoardCriteriaEnum.CaseVolume.Value,
                organizationGuid: this.props.zoneContext.zoneGuid,
                viewDataTypes: [
                    BoardViewDataTypesEnum.State.Value,
                    BoardViewDataTypesEnum.Jurisdiction.Value,
                    BoardViewDataTypesEnum.PlaintiffsFirm.Value,
                    BoardViewDataTypesEnum.Diagnosis.Value,
                    BoardViewDataTypesEnum.Timeline.Value,
                ],
            },
            readyToRenderReport: false,
            refreshReport: false,
            pendingResponse: true,
            saveToFavourites: false,
            userBoardSetting: {
                typeId: 2,
                settingsJson: '',
            },
            initComments: [],
            comments: [],
            showCommentsModal: false,
            showExportModal: false,
            caseStatuses: [],
            selectedCaseStatuses: [],
        };
    }

    componentDidMount() {
        if (
            !Authorization.isAuthorizedToRoute(
                LocalRoutes.Board,
                this.props.user,
                this.props.zoneContext.zoneGuid
            )
        )
            window.location.assign(LocalRoutes.AccessDenied);

        this.setInitialBoardState(ParamUtils.getParameterByName('preferences'));
        this.loadLoggedInUserDefaultOrganization();
        this.loadBoardComments();
        this.loadCaseStatuses();
    }

    loadLoggedInUserDefaultOrganization = async () => {
        const response =
            await this.props.zoneContext.zoneApiClient.getCurrentUserOrganizationFromServer(
                this.props.zoneContext.zoneGuid
            );
        if (!response) return;
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }

        this.setState({
            defaultOrganization: response.payload!,
            pendingResponse: false,
        });
    };

    setInitialBoardState = async (preferences: string) => {
        let boardPreferencesJSONString = '';
        // Regardless of whether user settings exist for the user, Preferences passed in query string take precedence over saved preferences.
        if (preferences) {
            boardPreferencesJSONString = atob(preferences);
            if (boardPreferencesJSONString) {
                const userBoardPreferences: IBoardParametersModel = JSON.parse(
                    boardPreferencesJSONString
                );
                const initBoardPreferences: IBoardParametersModel = JSON.parse(
                    boardPreferencesJSONString
                );
                // Check to make OrganizationGuidsure the Json is still valid and it at least set the basic parameters
                if (userBoardPreferences.statusId > 0 && userBoardPreferences.criteria > 0) {
                    const readyToRenderReport: boolean =
                        userBoardPreferences.date &&
                        (userBoardPreferences.date.startDate || userBoardPreferences.date.endDate)
                            ? true
                            : false;
                    this.setState({
                        initBoardPreferences: initBoardPreferences,
                        boardPreferences: userBoardPreferences,
                        readyToRenderReport: readyToRenderReport,
                        showSettingsModal: !readyToRenderReport,
                    });
                }
            }
        }
    };

    loadBoardComments = async () => {
        const response = await this.props.zoneContext.zoneApiClient.getBoardComments();
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }

        const comments = response.payload!;
        this.setState({
            initComments: JSON.parse(JSON.stringify(comments)),
            comments: comments,
            pendingResponse: false,
        });
    };

    loadCaseStatuses = async () => {
        const response = await _apiClient.getCaseStatusLookupData();
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }

        const caseStatusesToInclude = response.payload!.filter(
            (x) =>
                x.status &&
                (x.status.id === CaseStatusTypesEnum.Open.Value ||
                    x.status.id === CaseStatusTypesEnum.Resolved.Value)
        );
        const initialSelectedCaseStatuses = caseStatusesToInclude ? this.getInitialCaseStatusList(caseStatusesToInclude) : [];
        this.setState({ caseStatuses: caseStatusesToInclude, pendingResponse: false, selectedCaseStatuses:initialSelectedCaseStatuses });
    };

    getInitialCaseStatusList =(caseStatusesToInclude: ICaseStatusLookupModel[]) => {
        const statusLookupValuesList: IMultiSelectOption[] = [];
        caseStatusesToInclude.filter((a)=>a.status.id === CaseStatusTypesEnum.Open.Value).map((y) =>
            statusLookupValuesList.push({
                label: y.name,
                value: y.name!,
                id: y.id,
            })
        );
        return statusLookupValuesList;
    }

    showSettingsModal = () => {
        this.setState({ showSettingsModal: true, refreshReport: false, saveToFavourites: false });
    };

    cancelSettingsModal = () => {
        this.setState({
            boardPreferences: JSON.parse(JSON.stringify(this.state.initBoardPreferences)),
            showSettingsModal: false,
            validation: {},
            refreshReport: false,
            saveToFavourites: false,
        });
    };

    showCommentsModal = () => {
        this.setState({ showCommentsModal: true });
    };

    closeCommentsModal = () => {
        this.setState({
            comments: JSON.parse(JSON.stringify(this.state.initComments)),
            showCommentsModal: false,
        });
    };

    saveComments = async () => {
        this.setState({ pendingResponse: true });

        const response = await this.props.zoneContext.zoneApiClient.saveBoardComments(
            this.state.comments
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }

        this.setState({
            pendingResponse: false,
            showCommentsModal: false,
            initComments: JSON.parse(JSON.stringify(this.state.comments)),
        });
    };

    showExportModal = () => {
        this.setState({ showExportModal: true, validation: {} });
    };

    closeExportModal = () => {
        this.setState({ showExportModal: false, validation: {} });
    };

    generateAndDownloadReport = async () => {
        this.setState({ pendingResponse: true, validation: {} });

        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/' + ApiRoutes.ExportBoard, true);
        xhr.responseType = 'blob';
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('X-Tenant-Guid', this.props.zoneContext.zoneGuid);

        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
                // Reset Comments
                const comments = this.state.comments;
                for (let i = 0; i < comments.length; i++) {
                    comments[i].text = '';
                }

                this.setState({
                    pendingResponse: false,
                    showExportModal: false,
                    comments: comments,
                    initComments: comments,
                });

                let fileName = 'Board.pdf';
                const blob = xhr.response;

                const contentDisposition = xhr.getResponseHeader('Content-Disposition');
                if (contentDisposition) {
                    const contentDispositionItems = contentDisposition.split(';');
                    if (contentDispositionItems) {
                        for (let i = 0; i < contentDispositionItems.length; i++) {
                            const currentItem = contentDispositionItems[i];
                            if (currentItem.includes('filename=')) {
                                const n = currentItem.indexOf('filename=') + 9;
                                fileName = contentDispositionItems[i].substring(
                                    n + 1,
                                    contentDispositionItems[i].length - 1
                                );
                                break;
                            }
                        }
                    }
                }
                const a = document.createElement('a');
                a.href = window.URL.createObjectURL(blob);
                a.download = fileName;
                a.dispatchEvent(new MouseEvent('click'));
            }

            if (xhr.readyState === XMLHttpRequest.DONE && xhr.status >= 400) {
                const validation = this.state.validation;
                validation.model = [DisplayMessages.ReportGenerateError];
                this.setState({ pendingResponse: false, validation: validation });
            }
        };

        const boardExportPreferences = this.state.boardExportPreferences;
        // Always Copy boardPreferences to boardExportPreferences
        boardExportPreferences.statusId = this.state.boardPreferences.statusId;
        boardExportPreferences.subStatusIdList = this.state.boardPreferences.subStatusIdList;
        boardExportPreferences.subStatusIds = this.state.boardPreferences.subStatusIds;
        boardExportPreferences.criteria = this.state.boardPreferences.criteria;
        boardExportPreferences.organizationGuid = this.state.boardPreferences.organizationGuid;
        boardExportPreferences.date = JSON.parse(JSON.stringify(this.state.boardPreferences.date));

        xhr.send(JSON.stringify(boardExportPreferences));
    };

    refreshDashboard = async () => {
        const validation: IValidation = {};
        if (this.state.boardPreferences.statusId == 0) {
            validation.model = ['Status is required'];
            this.setState({ validation: validation });
            return;
        }

        if (this.state.boardPreferences.criteria == 0) {
            validation.model = ['Criteria is required'];
            this.setState({ validation: validation });
            return;
        }

        if (!this.state.boardPreferences.date) {
            validation.model = ['Date Range is required'];
            this.setState({ validation: validation });
            return;
        }
        if (
            this.state.boardPreferences.date &&
            (!this.state.boardPreferences.date?.startDate ||
                !this.state.boardPreferences.date?.endDate)
        ) {
            validation.model = ['Start and End Date are both required'];
            this.setState({ validation: validation });
            return;
        }

        this.setState({
            initBoardPreferences: JSON.parse(JSON.stringify(this.state.boardPreferences)),
            refreshReport: true,
            showSettingsModal: false,
            saveToFavourites: false,
            readyToRenderReport: true,
            validation: {},
        });
    };

    isChecked(type: string, value: number) {
        switch (type) {
            case BoardFilterDateTypesEnum.AsOfDate.Name:
            case BoardFilterDateTypesEnum.ServiceDate.Name:
            case BoardFilterDateTypesEnum.ResolutionDate.Name:
            case BoardFilterDateTypesEnum.TrialDate.Name:
            case BoardFilterDateTypesEnum.ProjectedSettlementDate.Name:
                return (
                    this.state.boardPreferences.date &&
                    value == this.state.boardPreferences.date.type
                );
            case 'Criteria':
                return value == this.state.boardPreferences.criteria;
            case 'ViewDataType':
                return this.state.boardExportPreferences.viewDataTypes.indexOf(value) > -1;
            case 'IncludeComments':
                return this.state.boardExportPreferences.includeComment;
            case 'BoardCaseDetailsExportTypes':
                return (
                    this.state.boardExportPreferences.viewDataTypes.indexOf(
                        BoardViewDataTypesEnum.CaseDetails.Value
                    ) > -1 && this.state.boardExportPreferences.boardCaseDetailsExportType == value
                );
            default:
                return false;
        }
    }

    handleCheckedChange(event: React.ChangeEvent<HTMLInputElement>, type: string, value: number) {
        const target = event.target;
        const checked = target.checked;

        const boardPreferences = this.state.boardPreferences;
        const boardExportPreferences = this.state.boardExportPreferences;

        switch (type) {
            case BoardFilterDateTypesEnum.AsOfDate.Name:
                if (checked) {
                    if (!boardPreferences.date) boardPreferences.date = {};
                    boardPreferences.date.type = BoardFilterDateTypesEnum.AsOfDate.Value;
                    boardPreferences.date.startDate = undefined;
                    boardPreferences.date.endDate = undefined;
                } else if (
                    boardPreferences.date &&
                    boardPreferences.date.type == BoardFilterDateTypesEnum.AsOfDate.Value
                ) {
                    boardPreferences.date.type = 0;
                    boardPreferences.date.startDate = undefined;
                    boardPreferences.date.endDate = undefined;
                }
                break;
            case BoardFilterDateTypesEnum.ServiceDate.Name:
                if (checked) {
                    if (!boardPreferences.date) boardPreferences.date = {};
                    boardPreferences.date.type = BoardFilterDateTypesEnum.ServiceDate.Value;
                    boardPreferences.date.startDate = undefined;
                    boardPreferences.date.endDate = undefined;
                } else if (
                    boardPreferences.date &&
                    boardPreferences.date.type == BoardFilterDateTypesEnum.ServiceDate.Value
                ) {
                    boardPreferences.date.type = 0;
                    boardPreferences.date.startDate = undefined;
                    boardPreferences.date.endDate = undefined;
                }
                break;
            case BoardFilterDateTypesEnum.ResolutionDate.Name:
                if (checked) {
                    if (!boardPreferences.date) boardPreferences.date = {};
                    boardPreferences.date.type = BoardFilterDateTypesEnum.ResolutionDate.Value;
                    boardPreferences.date.startDate = undefined;
                    boardPreferences.date.endDate = undefined;
                } else if (
                    boardPreferences.date &&
                    boardPreferences.date.type == BoardFilterDateTypesEnum.ResolutionDate.Value
                ) {
                    boardPreferences.date.type = 0;
                    boardPreferences.date.startDate = undefined;
                    boardPreferences.date.endDate = undefined;
                }
                break;
            case BoardFilterDateTypesEnum.TrialDate.Name:
                if (checked) {
                    if (!boardPreferences.date) boardPreferences.date = {};
                    boardPreferences.date.type = BoardFilterDateTypesEnum.TrialDate.Value;
                    boardPreferences.date.startDate = undefined;
                    boardPreferences.date.endDate = undefined;
                } else if (
                    boardPreferences.date &&
                    boardPreferences.date.type == BoardFilterDateTypesEnum.TrialDate.Value
                ) {
                    boardPreferences.date.type = 0;
                    boardPreferences.date.startDate = undefined;
                    boardPreferences.date.endDate = undefined;
                }
                break;
            case BoardFilterDateTypesEnum.ProjectedSettlementDate.Name:
                if (checked) {
                    if (!boardPreferences.date) boardPreferences.date = {};
                    boardPreferences.date.type =
                        BoardFilterDateTypesEnum.ProjectedSettlementDate.Value;
                    boardPreferences.date.startDate = undefined;
                    boardPreferences.date.endDate = undefined;
                } else if (
                    boardPreferences.date &&
                    boardPreferences.date.type ==
                        BoardFilterDateTypesEnum.ProjectedSettlementDate.Value
                ) {
                    boardPreferences.date.type = 0;
                    boardPreferences.date.startDate = undefined;
                    boardPreferences.date.endDate = undefined;
                }
                break;
            case 'Criteria':
                boardPreferences.criteria = checked ? value : 0;
                // Since Criteria is changing, Reset Date Range
                boardPreferences.date = { type: 0 };
                break;
            case 'SaveToFavourites':
                this.setState({ saveToFavourites: checked });
                return;
            case 'ViewDataType':
                if (checked) {
                    boardExportPreferences.viewDataTypes.push(value);
                    if (value == BoardViewDataTypesEnum.CaseDetails.Value)
                        boardExportPreferences.boardCaseDetailsExportType =
                            BoardCaseDetailsExportTypes.PDF.Value;
                } else {
                    const index = boardExportPreferences.viewDataTypes.indexOf(value);
                    if (index > -1) boardExportPreferences.viewDataTypes.splice(index, 1);
                }
                break;
            case 'IncludeComments':
                boardExportPreferences.includeComment = checked;
                break;
            case 'BoardCaseDetailsExportTypes':
                if (checked) {
                    boardExportPreferences.boardCaseDetailsExportType = value;
                } else {
                    boardExportPreferences.boardCaseDetailsExportType =
                        BoardCaseDetailsExportTypes.PDF.Value;
                }
                break;
            default:
                break;
        }

        this.setState({
            boardPreferences: boardPreferences,
            boardExportPreferences: boardExportPreferences,
        });
    }

    handleChangeDate = (event: React.ChangeEvent<HTMLInputElement>, dateType: number) => {
        const boardPreferences = this.state.boardPreferences;
        if (dateType == DateTypesEnum.StartDate) {
            boardPreferences.date!.startDate = event.target.value;
            this.setState({ boardPreferences: boardPreferences });
        } else if (dateType == DateTypesEnum.EndDate) {
            boardPreferences.date!.endDate = event.target.value;
            this.setState({ boardPreferences: boardPreferences });
        }
    };


    handleCommentsChange = (event: React.ChangeEvent<HTMLTextAreaElement>, dataType: number) => {
        const comments = this.state.comments;
        const match = comments.find((x) => x.boardViewDataType == dataType);
        if (match) {
            match.text = event.target.value;
            this.setState({ comments: comments });
        }
    };

    getBoardViewDataTypeName(value: number) {
        switch (value) {
            case BoardViewDataTypesEnum.State.Value:
                return BoardViewDataTypesEnum.State.DisplayName;
            case BoardViewDataTypesEnum.Jurisdiction.Value:
                return BoardViewDataTypesEnum.Jurisdiction.DisplayName;
            case BoardViewDataTypesEnum.PlaintiffsFirm.Value:
                return BoardViewDataTypesEnum.PlaintiffsFirm.DisplayName;
            case BoardViewDataTypesEnum.Diagnosis.Value:
                return BoardViewDataTypesEnum.Diagnosis.DisplayName;
            case BoardViewDataTypesEnum.Timeline.Value:
                return BoardViewDataTypesEnum.Timeline.DisplayName;
            default:
                return '';
        }
    }

    isValidCriteria(value: number) {
        const subStatusIds: number[] = cloneDeep(this.state.boardPreferences.subStatusIdList) ?? [];

        switch (value) {
            case BoardCriteriaEnum.CaseVolume.Value:
                return true;
            case BoardCriteriaEnum.ProjectedSettlement.Value:
                return this.state.boardPreferences.statusId === CaseStatusTypesEnum.Open.Value;
            case BoardCriteriaEnum.ActualSettlement.Value:
                return (
                    this.state.boardPreferences.statusId === CaseStatusTypesEnum.Resolved.Value &&
                    subStatusIds.length == 1 &&
                    subStatusIds[0] === CaseSubStatusTypesEnum.Settled.Value
                );
            default:
                return false;
        }
    }

    isValidDateRange(value: number) {
        switch (value) {
            case BoardFilterDateTypesEnum.AsOfDate.Value:
                return (
                    this.state.boardPreferences.criteria == BoardCriteriaEnum.CaseVolume.Value &&
                    this.state.boardPreferences.statusId === CaseStatusTypesEnum.Open.Value
                );
            case BoardFilterDateTypesEnum.ServiceDate.Value:
            case BoardFilterDateTypesEnum.TrialDate.Value:
                return this.state.boardPreferences.criteria == BoardCriteriaEnum.CaseVolume.Value;
            case BoardFilterDateTypesEnum.ProjectedSettlementDate.Value:
                return (
                    this.state.boardPreferences.criteria ==
                        BoardCriteriaEnum.ProjectedSettlement.Value &&
                    this.state.boardPreferences.statusId === CaseStatusTypesEnum.Open.Value
                );
            case BoardFilterDateTypesEnum.ResolutionDate.Value:
                return (
                    (this.state.boardPreferences.criteria == BoardCriteriaEnum.CaseVolume.Value &&
                        this.state.boardPreferences.statusId ===
                            CaseStatusTypesEnum.Resolved.Value) ||
                    (this.state.boardPreferences.criteria ==
                        BoardCriteriaEnum.ActualSettlement.Value &&
                        this.state.boardPreferences.statusId ===
                            CaseStatusTypesEnum.Resolved.Value &&
                        this.state.boardPreferences.subStatusIdList &&
                        this.state.boardPreferences.subStatusIdList.length == 1 &&
                        this.state.boardPreferences.subStatusIdList[0] ===
                            CaseSubStatusTypesEnum.Settled.Value)
                );
            default:
                return false;
        }
    }

    getStatusLookupValues = () => {
        const statusLookupValues: IMultiSelectOption[] = [];

        for (let index = 0; index < this.state.caseStatuses.length; index++) {
            const element = this.state.caseStatuses[index];
            statusLookupValues.push({
                label: element.name!,
                value: element.name!,
                id: element.id,
            });
        }
        return statusLookupValues;
    };
    

    handleStatusValuesSelected = (optionList: any) => {

        const boardPreferences = this.state.boardPreferences;
        const selectedStatusListCopy: IMultiSelectOption[] = [];
        const existingSelectedStatuss = cloneDeep(this.state.selectedCaseStatuses);
        //if exisitng has open status and if user selects resovled type then remove all open status related
        //and vice versa and only one status type can be selected either Open or Resolved.
        //since to make it similar to existing functionality. 
        
        const openStatusIds = this.state.caseStatuses.filter((a)=> a.status.id === CaseStatusTypesEnum.Open.Value).map((a)=>a.id);
        const resolvedStatusIds = this.state.caseStatuses.filter((a)=> a.status.id === CaseStatusTypesEnum.Resolved.Value).map((a)=>a.id);

        let hasAnyOpenStatusMatch = false;
        let hasAnyResolvedStatusMatch = false;

        if (existingSelectedStatuss) {
            hasAnyOpenStatusMatch = openStatusIds.some((statusId) =>
                existingSelectedStatuss.map((a) => a.id).includes(statusId)
            );
            if (hasAnyOpenStatusMatch) {
                if (optionList) {
                    hasAnyResolvedStatusMatch = resolvedStatusIds.some((statusId) =>
                        optionList.map((a: any) => a.id).includes(statusId)
                    );
                    if(hasAnyResolvedStatusMatch){
                        hasAnyOpenStatusMatch = false;
                    }
                }
            } else {
                hasAnyResolvedStatusMatch = resolvedStatusIds.some((statusId) =>
                    existingSelectedStatuss.map((a) => a.id).includes(statusId)
                );
                if (hasAnyResolvedStatusMatch) {
                    if (optionList) {
                        hasAnyOpenStatusMatch = openStatusIds.some((statusId) =>
                            optionList.map((a: any) => a.id).includes(statusId)
                        );
                        if(hasAnyOpenStatusMatch){
                            hasAnyResolvedStatusMatch = false;
                        }
                    }
                }
            }
        }

        const existingStatusIds: number[] = [];
        const existingSubStatusIds: number[] = [];
        
        let filteredOptions: IMultiSelectOption[] = optionList;
        if(hasAnyOpenStatusMatch){
            filteredOptions = optionList.filter( (a:any)=> openStatusIds.includes(a.id));
        }
        else if(hasAnyResolvedStatusMatch){
            filteredOptions = optionList.filter( (a:any)=> resolvedStatusIds.includes(a.id));
        }

        for (let i = 0; i < filteredOptions.length; i++) {
            const option = filteredOptions[i];
            if (option) {
                selectedStatusListCopy.push(option);
                const incomingStatusIds = this.getStatusIds(option);
                if (incomingStatusIds) {
                    incomingStatusIds.forEach((num) => {
                        if (!existingStatusIds.includes(num)) {
                            existingStatusIds.push(num);
                        }
                    });
                }
                const incomingSubStatusIds = this.getSubStatusIds(option);
                if (incomingSubStatusIds) {
                    incomingSubStatusIds.forEach((num) => {
                        if (!existingSubStatusIds.includes(num)) {
                            existingSubStatusIds.push(num);
                        }
                    });
                }
            }
        }

        boardPreferences.subStatusIdList = existingSubStatusIds && existingSubStatusIds.length > 0 ? existingSubStatusIds : [];
        boardPreferences.subStatusIds =  existingSubStatusIds && existingSubStatusIds.length > 0 ? existingSubStatusIds.join(',') : '';
        boardPreferences.statusId = existingStatusIds && existingSubStatusIds.length > 0  ? existingStatusIds[0] : 0; //todo need to be set array if required.
        boardPreferences.criteria = BoardCriteriaEnum.CaseVolume.Value;
        boardPreferences.date = { type: 0 };

        this.setState({
            boardPreferences: boardPreferences,
            selectedCaseStatuses: selectedStatusListCopy,
        });
    };

    getStatusIds = (selectedOption: any) => {
        const statusIds: number[] = [];
        const caseStatusItems = this.state.caseStatuses.filter((x) => x.id == selectedOption.id); //only one will come
        if (caseStatusItems) {
            caseStatusItems.map((item) => {
                if (!statusIds.includes(item.status.id)) {
                    statusIds.push(item.status!.id);
                }
            });
        }
        return statusIds;
    };

    getSubStatusIds = (selectedOption: any) => {
        const subStatusIds: number[] = [];
        const caseStatusItems = this.state.caseStatuses.filter((x) => x.id == selectedOption.id); //only one will come
        if (caseStatusItems) {
            caseStatusItems.map((item) => {
                if (!subStatusIds.includes(item.subStatus!.id)) {
                    subStatusIds.push(item.subStatus!.id);
                }
            });
        }

        return subStatusIds;
    };


    render() {
        if (this.state.pendingResponse) return <Loader />;

        return (
            <div>
                <DocumentTitle title="Board" />
                <h1 style={{ marginLeft: '10px' }}>
                    {this.state.defaultOrganization.shortName}
                    <BoardPreferencesHeader
                        boardPreferences={this.state.boardPreferences}
                        includeCriteria={true}
                        caseStatuses={this.state.caseStatuses}
                    />
                    {/*<span className="btn btn-blue float-end"> <Link to={LocalRoutes.UserReportList} style={{ color: "white" }}>View Existing Reports</Link></span>*/}
                    {this.state.comments.length > 0 ? (
                        <button
                            className="btn btn-orange float-end"
                            onClick={this.showCommentsModal}
                        >
                            Comments
                        </button>
                    ) : null}
                    {this.state.readyToRenderReport ? (
                        <button
                            className="btn btn-no-bg float-end text-gray"
                            onClick={this.showExportModal}
                        >
                            <i className="fal fa-file-chart-column" />
                            &nbsp;Report
                        </button>
                    ) : null}
                    <button
                        className="btn btn-no-bg float-end text-gray"
                        onClick={this.showSettingsModal}
                    >
                        <i className="fal fa-user-cog" />
                        &nbsp;Settings
                    </button>
                    {(Authorization.userHasRight(
                        UserRightsEnum.ViewCaseFileBasic,
                        this.props.user,
                        this.props.zoneContext.zoneGuid
                    ) ||
                        Authorization.userHasRight(
                            UserRightsEnum.ViewCaseFile,
                            this.props.user,
                            this.props.zoneContext.zoneGuid
                        )) &&
                    this.state.readyToRenderReport ? (
                        <Link
                            to={this.props.zoneContext.buildZoneUrl(
                                LocalRoutes.BoardCaseDetail +
                                    '?preferences=' +
                                    btoa(JSON.stringify(this.state.boardPreferences))
                            )}
                        >
                            <span className="btn btn-no-bg float-end text-gray">
                                <i className="fal fa-ballot" />
                                &nbsp;Case Details
                            </span>
                        </Link>
                    ) : null}
                </h1>

                {this.state.readyToRenderReport ? (
                    <Html5ReportViewer
                        reportName="Board.trdp"
                        reportParameters={BoardHelper.getBoardReportParameters(
                            this.state.boardPreferences,
                            undefined,
                            undefined,
                            this.props.zoneContext.zoneGuid
                        )}
                        refreshReport={this.state.refreshReport}
                    />
                ) : null}

                {this.state.showSettingsModal ? (
                    <Modal
                        centered
                        size="lg"
                        show={this.state.showSettingsModal}
                        onHide={this.cancelSettingsModal}
                        backdrop={false}
                        dialogClassName="board-settings-dialog"
                    >
                        <Modal.Header>
                            <Modal.Title> Board Settings </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="container-fluid">
                                <div className="mb-2 row">
                                    <span className="text-danger">
                                        {this.state.validation.model}
                                    </span>
                                </div>
                                <div className="row">
                                    <label className="control-label col-sm-2" htmlFor="caseStatus">
                                        Status
                                    </label>
                                    <div className="col-sm-6">
                                        <CheckmateSelect
                                            isMulti={true}
                                            options={this.getStatusLookupValues()}
                                            onChange={this.handleStatusValuesSelected}
                                            value={this.state.selectedCaseStatuses}
                                            placeholder="-- Select --"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-12 vertical-padding-med">
                                    <label className="control-label" htmlFor="caseStatus">
                                        Criteria
                                    </label>
                                    <div>
                                        <input
                                            id="chkCriteriaCaseVolume"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={this.isChecked(
                                                'Criteria',
                                                BoardCriteriaEnum.CaseVolume.Value
                                            )}
                                            disabled={
                                                !this.isValidCriteria(
                                                    BoardCriteriaEnum.CaseVolume.Value
                                                )
                                            }
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(
                                                    e,
                                                    'Criteria',
                                                    BoardCriteriaEnum.CaseVolume.Value
                                                );
                                            }}
                                        />
                                        <span
                                            className={
                                                this.isValidCriteria(
                                                    BoardCriteriaEnum.CaseVolume.Value
                                                )
                                                    ? ''
                                                    : 'grayed-out'
                                            }
                                        >
                                            &nbsp;&nbsp;{BoardCriteriaEnum.CaseVolume.Name}
                                        </span>
                                    </div>
                                    <div>
                                        <input
                                            id="chkCriteriaProjSettlement"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={this.isChecked(
                                                'Criteria',
                                                BoardCriteriaEnum.ProjectedSettlement.Value
                                            )}
                                            disabled={
                                                !this.isValidCriteria(
                                                    BoardCriteriaEnum.ProjectedSettlement.Value
                                                )
                                            }
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(
                                                    e,
                                                    'Criteria',
                                                    BoardCriteriaEnum.ProjectedSettlement.Value
                                                );
                                            }}
                                        />
                                        <span
                                            className={
                                                this.isValidCriteria(
                                                    BoardCriteriaEnum.ProjectedSettlement.Value
                                                )
                                                    ? ''
                                                    : 'grayed-out'
                                            }
                                        >
                                            &nbsp;&nbsp;{BoardCriteriaEnum.ProjectedSettlement.Name}
                                        </span>
                                    </div>
                                    <div>
                                        <input
                                            id="chkCriteriaActualSettlement"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={this.isChecked(
                                                'Criteria',
                                                BoardCriteriaEnum.ActualSettlement.Value
                                            )}
                                            disabled={
                                                !this.isValidCriteria(
                                                    BoardCriteriaEnum.ActualSettlement.Value
                                                )
                                            }
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(
                                                    e,
                                                    'Criteria',
                                                    BoardCriteriaEnum.ActualSettlement.Value
                                                );
                                            }}
                                        />
                                        <span
                                            className={
                                                this.isValidCriteria(
                                                    BoardCriteriaEnum.ActualSettlement.Value
                                                )
                                                    ? ''
                                                    : 'grayed-out'
                                            }
                                        >
                                            &nbsp;&nbsp;{BoardCriteriaEnum.ActualSettlement.Name}
                                        </span>
                                    </div>
                                </div>
                                <div className="col-sm-12 vertical-padding-med">
                                    <label className="control-label" htmlFor="dateRange">
                                        Date Range
                                    </label>
                                    <RangeSelector
                                        endDate={this.state.boardPreferences.date?.endDate}
                                        endDateFieldName="activeAsOfDateEndDate"
                                        htmlId="chkDateRangeTypeActiveAsOfDate"
                                        isCheckboxChecked={this.isChecked(
                                            BoardFilterDateTypesEnum.AsOfDate.Name,
                                            BoardFilterDateTypesEnum.AsOfDate.Value
                                        )}
                                        isCheckboxDisabled={
                                            !this.isValidDateRange(
                                                BoardFilterDateTypesEnum.AsOfDate.Value
                                            )
                                        }
                                        isValidRange={this.isValidDateRange(
                                            BoardFilterDateTypesEnum.AsOfDate.Value
                                        )}
                                        label={BoardFilterDateTypesEnum.AsOfDate.DisplayName}
                                        onCheckedChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleCheckedChange(
                                                e,
                                                BoardFilterDateTypesEnum.AsOfDate.Name,
                                                BoardFilterDateTypesEnum.AsOfDate.Value
                                            );
                                        }}
                                        onEndDateChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleChangeDate(e, DateTypesEnum.EndDate);
                                        }}
                                        onStartDateChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleChangeDate(e, DateTypesEnum.StartDate);
                                        }}
                                        startDate={this.state.boardPreferences.date?.startDate}
                                        startDateFieldName="activeAsOfDateStartDate"
                                    />

                                    <RangeSelector
                                        endDate={this.state.boardPreferences.date?.endDate}
                                        endDateFieldName="serviceDateStartDate"
                                        htmlId="chkDateRangeTypeServiceDate"
                                        isCheckboxChecked={this.isChecked(
                                            BoardFilterDateTypesEnum.ServiceDate.Name,
                                            BoardFilterDateTypesEnum.ServiceDate.Value
                                        )}
                                        isCheckboxDisabled={
                                            !this.isValidDateRange(
                                                BoardFilterDateTypesEnum.ServiceDate.Value
                                            )
                                        }
                                        isValidRange={this.isValidDateRange(
                                            BoardFilterDateTypesEnum.ServiceDate.Value
                                        )}
                                        label={BoardFilterDateTypesEnum.ServiceDate.DisplayName}
                                        onCheckedChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleCheckedChange(
                                                e,
                                                BoardFilterDateTypesEnum.ServiceDate.Name,
                                                BoardFilterDateTypesEnum.ServiceDate.Value
                                            );
                                        }}
                                        onEndDateChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleChangeDate(e, DateTypesEnum.EndDate);
                                        }}
                                        onStartDateChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleChangeDate(e, DateTypesEnum.StartDate);
                                        }}
                                        startDate={this.state.boardPreferences.date?.startDate}
                                        startDateFieldName="serviceDateEndDate"
                                    />

                                    <RangeSelector
                                        endDate={this.state.boardPreferences.date?.endDate}
                                        endDateFieldName="dateOfDismissalStartDate"
                                        htmlId="chkDateRangeTypeDateOfDismissal"
                                        isCheckboxChecked={this.isChecked(
                                            BoardFilterDateTypesEnum.ResolutionDate.Name,
                                            BoardFilterDateTypesEnum.ResolutionDate.Value
                                        )}
                                        isCheckboxDisabled={
                                            !this.isValidDateRange(
                                                BoardFilterDateTypesEnum.ResolutionDate.Value
                                            )
                                        }
                                        isValidRange={this.isValidDateRange(
                                            BoardFilterDateTypesEnum.ResolutionDate.Value
                                        )}
                                        label={BoardFilterDateTypesEnum.ResolutionDate.DisplayName}
                                        onCheckedChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleCheckedChange(
                                                e,
                                                BoardFilterDateTypesEnum.ResolutionDate.Name,
                                                BoardFilterDateTypesEnum.ResolutionDate.Value
                                            );
                                        }}
                                        onEndDateChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleChangeDate(e, DateTypesEnum.EndDate);
                                        }}
                                        onStartDateChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleChangeDate(e, DateTypesEnum.StartDate);
                                        }}
                                        startDate={this.state.boardPreferences.date?.startDate}
                                        startDateFieldName="dateOfDismissalEndDate"
                                    />

                                    <RangeSelector
                                        endDate={this.state.boardPreferences.date?.endDate}
                                        endDateFieldName="trialDateEndDate"
                                        htmlId="chkDateRangeTypeTrialDate"
                                        isCheckboxChecked={this.isChecked(
                                            BoardFilterDateTypesEnum.TrialDate.Name,
                                            BoardFilterDateTypesEnum.TrialDate.Value
                                        )}
                                        isCheckboxDisabled={
                                            !this.isValidDateRange(
                                                BoardFilterDateTypesEnum.TrialDate.Value
                                            )
                                        }
                                        isValidRange={this.isValidDateRange(
                                            BoardFilterDateTypesEnum.TrialDate.Value
                                        )}
                                        label={BoardFilterDateTypesEnum.TrialDate.DisplayName}
                                        onCheckedChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleCheckedChange(
                                                e,
                                                BoardFilterDateTypesEnum.TrialDate.Name,
                                                BoardFilterDateTypesEnum.TrialDate.Value
                                            );
                                        }}
                                        onEndDateChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleChangeDate(e, DateTypesEnum.EndDate);
                                        }}
                                        onStartDateChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleChangeDate(e, DateTypesEnum.StartDate);
                                        }}
                                        startDate={this.state.boardPreferences.date?.startDate}
                                        startDateFieldName="trialDateStartDate"
                                    />

                                    <RangeSelector
                                        endDate={this.state.boardPreferences.date?.endDate}
                                        endDateFieldName="projectedSettlementDateEndDate"
                                        htmlId="chkDateRangeTypeProjectedSettlementDate"
                                        isCheckboxChecked={this.isChecked(
                                            BoardFilterDateTypesEnum.ProjectedSettlementDate.Name,
                                            BoardFilterDateTypesEnum.ProjectedSettlementDate.Value
                                        )}
                                        isCheckboxDisabled={
                                            !this.isValidDateRange(
                                                BoardFilterDateTypesEnum.ProjectedSettlementDate
                                                    .Value
                                            )
                                        }
                                        isValidRange={this.isValidDateRange(
                                            BoardFilterDateTypesEnum.ProjectedSettlementDate.Value
                                        )}
                                        label={
                                            BoardFilterDateTypesEnum.ProjectedSettlementDate
                                                .DisplayName
                                        }
                                        onCheckedChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleCheckedChange(
                                                e,
                                                BoardFilterDateTypesEnum.ProjectedSettlementDate
                                                    .Name,
                                                BoardFilterDateTypesEnum.ProjectedSettlementDate
                                                    .Value
                                            );
                                        }}
                                        onEndDateChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleChangeDate(e, DateTypesEnum.EndDate);
                                        }}
                                        onStartDateChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            this.handleChangeDate(e, DateTypesEnum.StartDate);
                                        }}
                                        startDate={this.state.boardPreferences.date?.startDate}
                                        startDateFieldName="projectedSettlementDateStartDate"
                                    />
                                </div>
                            </div>
                            <div className="dialog-btn-div margin-top-sm">
                                <button
                                    className="btn btn-default float-end"
                                    onClick={this.cancelSettingsModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-orange float-end"
                                    onClick={this.refreshDashboard}
                                >
                                    Go
                                </button>
                                {/*<input id="chkSaveToFav" type="checkbox" className="form-check-input" onChange={(e: React.ChangeEvent<HTMLInputElement>) => { this.handleCheckedChange(e, 'SaveToFavourites', 0) }} /><span>&nbsp;&nbsp;Save to Favourites</span>*/}
                            </div>
                        </Modal.Body>
                    </Modal>
                ) : null}

                {this.state.showCommentsModal ? (
                    <Modal
                        centered
                        show={this.state.showCommentsModal}
                        onHide={this.closeCommentsModal}
                        backdrop={false}
                    >
                        <Modal.Header>
                            <Modal.Title> Comments </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {this.state.comments.map((comment: IBoardComment) => {
                                return (
                                    <div className="form-group" style={{ paddingLeft: '10px' }}>
                                        <label className="control-label">
                                            {this.getBoardViewDataTypeName(
                                                comment.boardViewDataType
                                            )}
                                        </label>
                                        <textarea
                                            className="form-control"
                                            value={comment.text}
                                            onChange={(
                                                e: React.ChangeEvent<HTMLTextAreaElement>
                                            ) => {
                                                this.handleCommentsChange(
                                                    e,
                                                    comment.boardViewDataType
                                                );
                                            }}
                                        />
                                    </div>
                                );
                            })}
                            <div className="dialog-btn-div margin-top-sm">
                                <button
                                    className="btn btn-default float-end"
                                    onClick={this.closeCommentsModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-orange float-end"
                                    onClick={this.saveComments}
                                >
                                    Save
                                </button>
                            </div>
                        </Modal.Body>
                    </Modal>
                ) : null}

                {this.state.showExportModal ? (
                    <Modal
                        centered
                        show={this.state.showExportModal}
                        onHide={this.closeExportModal}
                        backdrop={false}
                    >
                        <Modal.Header>
                            <Modal.Title> Export Settings</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div>
                                <span className="text-danger">{this.state.validation.model}</span>
                            </div>
                            <div className="container-fluid">
                                <div className="col-sm-12">
                                    <label className="control-label" htmlFor="exportViewDataTypes">
                                        Data
                                    </label>
                                    <div>
                                        <input
                                            id="chkViewDataTypeState"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={this.isChecked(
                                                'ViewDataType',
                                                BoardViewDataTypesEnum.State.Value
                                            )}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(
                                                    e,
                                                    'ViewDataType',
                                                    BoardViewDataTypesEnum.State.Value
                                                );
                                            }}
                                        />
                                        <span>&nbsp;&nbsp;{BoardViewDataTypesEnum.State.Name}</span>
                                    </div>
                                    <div>
                                        <input
                                            id="chkViewDataTypeJurisdiction"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={this.isChecked(
                                                'ViewDataType',
                                                BoardViewDataTypesEnum.Jurisdiction.Value
                                            )}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(
                                                    e,
                                                    'ViewDataType',
                                                    BoardViewDataTypesEnum.Jurisdiction.Value
                                                );
                                            }}
                                        />
                                        <span>
                                            &nbsp;&nbsp;{BoardViewDataTypesEnum.Jurisdiction.Name}
                                        </span>
                                    </div>
                                    <div>
                                        <input
                                            id="chkViewDataTypePlaintiffsFirm"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={this.isChecked(
                                                'ViewDataType',
                                                BoardViewDataTypesEnum.PlaintiffsFirm.Value
                                            )}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(
                                                    e,
                                                    'ViewDataType',
                                                    BoardViewDataTypesEnum.PlaintiffsFirm.Value
                                                );
                                            }}
                                        />
                                        <span>
                                            &nbsp;&nbsp;{BoardViewDataTypesEnum.PlaintiffsFirm.Name}
                                        </span>
                                    </div>
                                    <div>
                                        <input
                                            id="chkViewDataTypeDiagnosis"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={this.isChecked(
                                                'ViewDataType',
                                                BoardViewDataTypesEnum.Diagnosis.Value
                                            )}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(
                                                    e,
                                                    'ViewDataType',
                                                    BoardViewDataTypesEnum.Diagnosis.Value
                                                );
                                            }}
                                        />
                                        <span>
                                            &nbsp;&nbsp;{BoardViewDataTypesEnum.Diagnosis.Name}
                                        </span>
                                    </div>
                                    <div>
                                        <input
                                            id="chkViewDataTypeTimeline"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={this.isChecked(
                                                'ViewDataType',
                                                BoardViewDataTypesEnum.Timeline.Value
                                            )}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(
                                                    e,
                                                    'ViewDataType',
                                                    BoardViewDataTypesEnum.Timeline.Value
                                                );
                                            }}
                                        />
                                        <span>
                                            &nbsp;&nbsp;{BoardViewDataTypesEnum.Timeline.Name}
                                        </span>
                                    </div>
                                    <div>
                                        <input
                                            id="chkViewDataTypeCaseDetails"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={this.isChecked(
                                                'ViewDataType',
                                                BoardViewDataTypesEnum.CaseDetails.Value
                                            )}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(
                                                    e,
                                                    'ViewDataType',
                                                    BoardViewDataTypesEnum.CaseDetails.Value
                                                );
                                            }}
                                        />
                                        <span>
                                            &nbsp;&nbsp;{BoardViewDataTypesEnum.CaseDetails.Name}
                                        </span>
                                    </div>
                                    {this.isChecked(
                                        'ViewDataType',
                                        BoardViewDataTypesEnum.CaseDetails.Value
                                    ) ? (
                                        <div style={{ paddingLeft: '20px' }}>
                                            <div>
                                                <input
                                                    id="chkBoardCaseDetailsExportTypesPDF"
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={this.isChecked(
                                                        'BoardCaseDetailsExportTypes',
                                                        BoardCaseDetailsExportTypes.PDF.Value
                                                    )}
                                                    onChange={(
                                                        e: React.ChangeEvent<HTMLInputElement>
                                                    ) => {
                                                        this.handleCheckedChange(
                                                            e,
                                                            'BoardCaseDetailsExportTypes',
                                                            BoardCaseDetailsExportTypes.PDF.Value
                                                        );
                                                    }}
                                                />
                                                <span>
                                                    &nbsp;&nbsp;
                                                    {BoardCaseDetailsExportTypes.PDF.Name}
                                                </span>
                                            </div>
                                            <div>
                                                <input
                                                    id="chkBoardCaseDetailsExportTypesExcel"
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={this.isChecked(
                                                        'BoardCaseDetailsExportTypes',
                                                        BoardCaseDetailsExportTypes.Excel.Value
                                                    )}
                                                    onChange={(
                                                        e: React.ChangeEvent<HTMLInputElement>
                                                    ) => {
                                                        this.handleCheckedChange(
                                                            e,
                                                            'BoardCaseDetailsExportTypes',
                                                            BoardCaseDetailsExportTypes.Excel.Value
                                                        );
                                                    }}
                                                />
                                                <span>
                                                    &nbsp;&nbsp;
                                                    {BoardCaseDetailsExportTypes.Excel.Name}
                                                </span>
                                            </div>
                                            <div>
                                                <input
                                                    id="chkBoardCaseDetailsExportTypesBoth"
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={this.isChecked(
                                                        'BoardCaseDetailsExportTypes',
                                                        BoardCaseDetailsExportTypes.Both.Value
                                                    )}
                                                    onChange={(
                                                        e: React.ChangeEvent<HTMLInputElement>
                                                    ) => {
                                                        this.handleCheckedChange(
                                                            e,
                                                            'BoardCaseDetailsExportTypes',
                                                            BoardCaseDetailsExportTypes.Both.Value
                                                        );
                                                    }}
                                                />
                                                <span>
                                                    &nbsp;&nbsp;
                                                    {BoardCaseDetailsExportTypes.Both.Name}
                                                </span>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                                <div className="col-sm-12">
                                    <hr />
                                    <div>
                                        <input
                                            id="chkIncludeComments"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={this.isChecked('IncludeComments', -1)}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.handleCheckedChange(e, 'IncludeComments', -1);
                                            }}
                                        />
                                        <span>&nbsp;&nbsp;Include Comments</span>
                                    </div>
                                </div>
                            </div>
                            <div className="dialog-btn-div margin-top-sm">
                                <button
                                    className="btn btn-default float-end"
                                    onClick={this.closeExportModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-orange float-end"
                                    onClick={this.generateAndDownloadReport}
                                >
                                    Go
                                </button>
                            </div>
                        </Modal.Body>
                    </Modal>
                ) : null}
            </div>
        );
    }
}

interface IRangeSelector {
    htmlId: string;
    isCheckboxChecked: boolean | undefined;
    isCheckboxDisabled: boolean | undefined;
    isValidRange: boolean | undefined;
    label: string;
    onCheckedChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onEndDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onStartDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    startDate: string | undefined;
    endDate: string | undefined;
    startDateFieldName: string;
    endDateFieldName: string;
}

function RangeSelector(props: IRangeSelector) {
    return (
        <Stack direction="horizontal" gap={2}>
            <input
                id={props.htmlId}
                className="form-check-input"
                type="checkbox"
                checked={props.isCheckboxChecked}
                disabled={props.isCheckboxDisabled}
                onChange={props.onCheckedChange}
            />
            <span className={'padding-right ' + (props.isValidRange ? '' : 'grayed-out')}>
                {props.label}
            </span>
            {props.isCheckboxChecked && props.isValidRange && (
                <Stack direction="horizontal" gap={2} style={{ alignItems: 'center' }}>
                    <input
                        className={
                            'form-control ' + (props.startDate === '' ? 'unselectClass ' : '')
                        }
                        type="date"
                        name={props.startDateFieldName}
                        value={props.startDate ? Common.dateFormat(props.startDate) : ''}
                        onChange={props.onStartDateChange}
                    />
                    <span>to</span>
                    <input
                        className={'form-control ' + (props.endDate === '' ? 'unselectClass ' : '')}
                        type="date"
                        name={props.endDateFieldName}
                        value={props.endDate ? Common.dateFormat(props.endDate) : ''}
                        onChange={props.onEndDateChange}
                    />
                </Stack>
            )}
        </Stack>
    );
}
